<script setup lang="ts">
import { cn } from "@voltalis/frontend-utils";
import { nextTick, onMounted, useTemplateRef } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { ProgramColorName } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { keys } from "@/utils/object";
import { PROGRAM_COLORS } from "@/utils/programs/program-utils";

import InputText from "primevue/inputtext";

import ProgramIcon from "@/components/programming/program-icon.vue";

interface IProps {
  invalid: boolean;
  siteName: string;
  subtitle: string;
  autofocus?: boolean;
  disabled?: boolean;
  editable?: boolean;
  isProgramSiteDefault?: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  autofocus: false,
  disabled: false,
  editable: true,
});
const name = defineModel<string>("name", { required: true });
const programColor = defineModel<ProgramColorName>("color", { required: true });
const { t, k } = useTypedIntl();
const nameInput = useTemplateRef<{ $el?: HTMLInputElement; focus?: () => void }>("nameInput");

onMounted(async () => {
  if (!props.autofocus) return;

  await nextTick();
  const input = nameInput.value;
  const element = input?.$el ?? input;
  element?.focus?.();
});
</script>

<template>
  <div class="flex w-full gap-6">
    <section
      class="flex flex-col flex-grow rounded-xl border border-neutral-light bg-white px-6 py-[18px]"
      :data-test-id="DATA_TEST_IDS.components.programEditor.identityCard"
    >
      <div class="flex items-center gap-2.5">
        <ProgramIcon
          :program-color="programColor"
          :is-site-default="props.isProgramSiteDefault"
        />

        <template v-if="editable && !props.isProgramSiteDefault">
          <label
            class="sr-only"
            :data-test-id="DATA_TEST_IDS.components.programEditor.identityNameLabel"
            for="program-identity-name"
          >
            {{ t(k.control.programming.details.identity.programName) }}
          </label>
          <InputText
            id="program-identity-name"
            ref="nameInput"
            v-model.trim="name"
            :aria-invalid="invalid"
            :autofocus="autofocus"
            class="!h-auto w-full !border-0 !bg-transparent !p-0 !text-xl !font-semibold !text-text-primary !shadow-none"
            :data-test-id="DATA_TEST_IDS.components.programEditor.identityNameInput"
            :disabled="props.disabled"
            :invalid="invalid"
            :placeholder="t(k.control.programming.details.identity.programNamePlaceholder)"
            required
          />
        </template>
        <span
          v-else
          class="program-identity-name w-full text-xl font-semibold text-text-primary"
          :data-test-id="DATA_TEST_IDS.components.programEditor.identityNameText"
        >
          <template v-if="props.isProgramSiteDefault">
            {{ t(k.control.programming.defaultProgram) }}
          </template>
          <template v-else>
            {{ name }}
          </template>
        </span>
      </div>

      <p
        class="mt-2 text-sm text-text-secondary"
        :data-test-id="DATA_TEST_IDS.components.programEditor.identitySubtitle"
      >
        {{ props.subtitle }}
        <strong class="font-bold">{{ siteName }}</strong>
      </p>

      <small
        v-if="invalid"
        class="mt-2 block text-red-500"
        :data-test-id="DATA_TEST_IDS.components.programEditor.identityNameError"
      >
        {{ t(k.control.programming.details.identity.programNameRequired) }}
      </small>
    </section>

    <section
      v-if="props.editable && !props.isProgramSiteDefault"
      class="flex flex-col w-[266px] shrink-0 rounded-xl border border-neutral-light bg-white px-6 py-[18px] gap-4"
      :data-test-id="DATA_TEST_IDS.components.programEditor.programColorCard.card"
    >
      <span
        class="text-text-secondary font-semibold"
        :data-test-id="DATA_TEST_IDS.components.programEditor.programColorCard.title"
        >{{ t(k.control.programming.details.identity.programColor) }}</span
      >

      <div class="program-colors flex gap-3">
        <button
          v-for="color in keys(PROGRAM_COLORS)"
          :key="color"
          :class="
            cn('h-[34px] w-[34px] rounded-lg bg-gradient-to-br', [
              PROGRAM_COLORS[color].fromColor,
              PROGRAM_COLORS[color].toColor,
            ])
          "
          :data-test-id="DATA_TEST_IDS.components.programEditor.programColorCard.colorButton"
          :data-program-color="color"
          :disabled="props.disabled"
          @click="programColor = color"
        >
          <i
            v-if="programColor === color"
            class="pi pi-check text-white !text-xs"
          />
        </button>
      </div>
    </section>
  </div>
</template>
